import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';

import { Store } from '@ngrx/store';
import { isBoolean, isNumber, isObject, isString, isNull } from 'util';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { RouterReducerState } from '@ngrx/router-store';
import { get } from 'lodash';

import { LightboxService } from '@gosuite/lightbox';
import * as RouterActions from '../../../../shared/state-management/actions/router';
import { openNEOInNewTab } from '../../../../shared/state-management/reducers/index';

import * as selectors from '../../../../shared/state-management/reducers/index';
import * as SidebarActions from '../../../../shared/state-management/actions/sidebar';
import { WindowRef } from '../../../../shared/services/window-ref';
import { PrrlFormButlerTokenService } from '../../../../shared/components/prrl-form-butler-dialog/prrl-form-butler-token.service';
import { WebstartService } from '../../../../shared/webstart/webstart.service';
import { CloseNeoFrame } from '../../../../shared/state-management/actions/app-state';
import { Webstart } from '../../../../shared/webstart/models/webstart-response';
import { CrsService } from '../../../../xena/core/crs/crs.service';
import { isGtsAgency, showAdminForGts } from '../../../../shared/help/functions';



@Component({
	selector: 'plat-sidebar',
	templateUrl: './plat-sidebar.component.html',
	styleUrls: ['./plat-sidebar.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlatSidebarComponent implements OnDestroy, OnInit {
	private _onComponentDestroy$: Subject<boolean> = new Subject<boolean>();

	public isNavigationExpanded$: Observable<boolean> = this._store.select(selectors.isNavigationExpanded);
	public activeNavigationItem$: Observable<string> = this._store.select(selectors.getActiveNavigationItem);

	public isUserLoggedIn$: Observable<boolean> = this._store.select(selectors.getLoginState).map(state => isObject(state) && isBoolean(state.loggedIn) ? state.loggedIn : false);
	public isDashboardEnabled$: Observable<boolean> = Observable.of(false);
	public isXenaEnabled$: Observable<boolean> = Observable.of(false);
	public isNeoEnabled$: Observable<boolean> = Observable.of(false);
	public isBedsAndMoreEnabled$: Observable<boolean> = Observable.of(false);
	public isARGUSEnabled$: Observable<boolean> = Observable.of(false);
	public isURANIAAdminEnabled$: Observable<boolean> = Observable.of(false);
	public isSmartBookingEnabled$: Observable<boolean> = Observable.of(false);
	public showAdminForGTS$: Observable<boolean> = Observable.combineLatest(
		this._store.select(selectors.getSelectedAgency),
		this._store.select(selectors.getXenaConfig)
	).map(([agency, config]) => !isGtsAgency(agency) || showAdminForGts(config));

	public openedWithSSO: boolean = sessionStorage.getItem('openedWithSSO') === 'true';

	public constructor(
		private _vcr: ViewContainerRef,
		private _store: Store<selectors.State>,
		private _lightboxService: LightboxService,
		private _window: WindowRef,
		private _tokenService: PrrlFormButlerTokenService,
		private _webstart: WebstartService,
		private _crsService: CrsService
	) {}

	public ngOnDestroy(): void {
		this._onComponentDestroy$.next(true);
	}

	public ngOnInit(): void {

		const awaitLogin$ = this._store.select(selectors.getLoginState).filter(s => s.loggedIn === true).subscribe(() => {
			this.isDashboardEnabled$ = Observable.combineLatest(
				this._store.select(selectors.getSelectedProvider).map(providerId => isNumber(providerId) && providerId > 0 && providerId !== 2),
				this._store.select(selectors.isAllowedUserIfTMG),
				this._store.select(selectors.isBosys),
				(byProvider, byAgent, isBosys) => byProvider && byAgent && !isBosys,
			).takeUntil(this._onComponentDestroy$);

			this.isXenaEnabled$ = Observable.combineLatest(
				this._store.select(selectors.getXENADisabledState).map(disabled => !disabled),
				this._webstart.getCachedWebstartByIdentifier('xena').map(webstarts => (webstarts || []).length >= 1),
				(hasBtn, hasWebstart) => hasWebstart && hasBtn,
			).takeUntil(this._onComponentDestroy$);

			this.isARGUSEnabled$ = this._webstart.getCachedWebstartByIdentifier('argus.go-suite')
				.map(webstarts => (webstarts || []).length >= 1);

			this.isBedsAndMoreEnabled$ = this._webstart.getCachedWebstartByIdentifier('snowstorm')
				.map(webstarts => (webstarts || []).length >= 1);

			this.isURANIAAdminEnabled$ = Observable.combineLatest(
				this._webstart.getCachedWebstartByIdentifier('urania-admin.go-suite').map(webstarts => (webstarts || []).length >= 1),
				this._store.select(selectors.isAllowedUserIfTMG),
				(hasWebstart, isAllowedUserIfTMG) => hasWebstart && isAllowedUserIfTMG
			);

			this.isNeoEnabled$ = Observable.combineLatest(
				this._store.select(selectors.getNEODisabledState).map(disabled => !disabled),
				this._store.select(selectors.getXenaConfig).map(config => isNull(config) || isObject(config) && isObject(config.crs) && config.crs.NEO),
				this._store.select(selectors.getSelectedAgency).map(agency => isGtsAgency(agency)),
				(isNeoEnabled, isNeoViaConfigApiEnabled, isGTS) => isNeoEnabled && isNeoViaConfigApiEnabled && !isGTS
			);

			this.isSmartBookingEnabled$ = Observable.combineLatest(
				this._store.select(selectors.getSelectedAgency),
				this._webstart.hasAgentCachedWebstart('smart-booking')
			).switchMap(() => this._webstart.hasAgentCachedWebstart('smart-booking'));

			awaitLogin$.unsubscribe();
		});

		Observable.combineLatest(
			this._store.select(selectors.getAppState),
			this._store.select(selectors.showNeoFrame),
			this._store.select(selectors.getRouterState),
			this._store.select(selectors.showARGUSFrame),
			(a, s, r, ar) => ({ appState: a, showNeoFrame: s, route: r, showARGUSFrame: ar })
		).takeUntil(this._onComponentDestroy$).subscribe(res => {
			const activeRouteName: string = PlatSidebarComponent._getActiveRouteName(res.route);

			if (activeRouteName === '/home') {
				this._store.dispatch(new SidebarActions.SetActiveNavigationItem('home'));
				this._store.dispatch(new SidebarActions.SetActiveNavigationMenuItem(null));
			} else if (activeRouteName === '/NEO' || res.appState && res.showNeoFrame) {
				this._store.dispatch(new SidebarActions.SetActiveNavigationItem('neo'));
				this._store.dispatch(new SidebarActions.SetActiveNavigationMenuItem(null));
			} else if (activeRouteName === '/ARGUS' || res.appState && res.showARGUSFrame) {
				this._store.dispatch(new SidebarActions.SetActiveNavigationItem('argus'));
				this._store.dispatch(new SidebarActions.SetActiveNavigationMenuItem(null));
			} else if (activeRouteName === '/XENA' || activeRouteName === '/XENA/LEAFLET') {
				this._store.dispatch(new SidebarActions.SetActiveNavigationItem('xena'));
				this._store.dispatch(new SidebarActions.SetActiveNavigationMenuItem(null));
			} else if (activeRouteName === '/ADMIN') {
				this._store.dispatch(new SidebarActions.SetActiveNavigationItem('admin'));
				this._store.dispatch(new SidebarActions.SetActiveNavigationMenuItem(null));
			} else {
				this._store.dispatch(new SidebarActions.SetActiveNavigationItem(null));
				this._store.dispatch(new SidebarActions.SetActiveNavigationMenuItem(null));
			}
		});
	}

	public sidebarTrigger(): void {
		this.isNavigationExpanded$.take(1).subscribe(isExpanded => {
			if (isExpanded) {
				this._store.dispatch(new SidebarActions.CloseNavigation());
			} else {
				this._store.dispatch(new SidebarActions.OpenNavigation());
			}
		});
	}

	public closeMenu(closeNeoFrame: boolean = true): void {
		this._store.dispatch(new SidebarActions.CloseNavigation());

		if (closeNeoFrame) {
			this._store.dispatch(new CloseNeoFrame());
		}
	}

	public openBedsAndMore(): void {
		this._getWebstartUrlByIdentifier('snowstorm').take(1).subscribe(url => {
			if (url) {
				this._window.nativeWindow.open(url);
			}
		});
	}

	public openSmaboTransactions(): void {
		this._webstart.getWebstartForSelectedAgent('smart-booking').take(1).subscribe(webstart => {
			if (webstart) {
				this._crsService.openSmartBooking(webstart);
			}
		});
	}

	public openNEO(): void {
		this._store.dispatch(new RouterActions.OpenNEO());
	}

	private _getWebstartUrlByIdentifier(identifier: string): Observable<string> {
		return this._store.select(selectors.getSelectedAgency).switchMap(agency => this._webstart.getCachedWebstartByIdentifier(identifier).map(webstarts => {
			const webstartToAgency: Webstart = (webstarts || []).find(webstart => `${webstart.agentId}` === `${agency.agentId}`);

			if (get(webstartToAgency, 'url', null)) {
				return webstartToAgency.url;
			}

			if (get(webstarts, '[0].url', null)) {
				return webstarts[0].url;
			}

			return null;
		}));
	}

	private static _getActiveRouteName(route: RouterReducerState): string {
		let activeRouteName: string = '';

		if (isObject(route) && isObject(route.state) && isString(route.state.url)) {
			const routeParts: Array<string> = route.state.url.split('/');

			activeRouteName = routeParts.length > 0
				? routeParts.indexOf('admin')
					? routeParts.join('/').toUpperCase()
					: routeParts[routeParts.length - 1].toUpperCase()
				: route.state.url.toUpperCase();

			if (activeRouteName === '/') {
				activeRouteName = '/home';
			}
		}

		return activeRouteName;
	}
}



// WEBPACK FOOTER //
// ./src/app/layout/components/plat-sidebar/plat-sidebar/plat-sidebar.component.ts